'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { Illustration } from '@/ui/illustrations/illustration/Illustration'
import type { PropsWithChildren } from 'react'
import type { Placement, PlacementAxis } from 'react-aria'
import {
  Tooltip as AriaTooltip,
  TooltipTrigger as AriaTooltipTrigger,
  OverlayArrow,
  type TooltipTriggerComponentProps,
} from 'react-aria-components'

const TOOLTIP_OFFSET = 10

// pointer-arrow points down by default; box stays native 2:1 (h-1 w-2) to avoid SVG stretching,
// rotated per placement, with a small translate nudging it flush against the tooltip body
const ARROW_CLASSNAME_BY_PLACEMENT: Record<PlacementAxis, string> = {
  top: 'h-1 w-2 -translate-y-px rotate-0',
  bottom: 'h-1 w-2 translate-y-px rotate-180',
  left: 'h-1 w-2 -translate-x-[0.3rem] -rotate-90',
  right: 'h-1 w-2 translate-x-[0.3rem] rotate-90',
  center: 'h-1 w-2 -translate-y-px rotate-0',
}

function TooltipArrow({
  placement,
  dataTestId,
}: {
  placement: PlacementAxis | null
  dataTestId?: string
}) {
  return (
    <Illustration
      name="pointer-arrow"
      className={twMerge('block text-info-bg', ARROW_CLASSNAME_BY_PLACEMENT[placement ?? 'top'])}
      aria-hidden
      data-testid={`${dataTestId ?? 'tooltip'}/arrow`}
    />
  )
}

export type TooltipProps = PropsWithChildren<
  UiComponentProps & {
    /** Position of the tooltip relative to its trigger */
    placement: Placement

    /** Whether the arrow is displayed */
    showArrow?: boolean

    /** Distance between the trigger and the tooltip */
    offset?: number
  }
>

export function TooltipTrigger(props: TooltipTriggerComponentProps) {
  return <AriaTooltipTrigger {...props} />
}

export function Tooltip({
  children,
  id,
  className,
  placement,
  showArrow = true,
  offset = TOOLTIP_OFFSET,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
}: TooltipProps) {
  return (
    <AriaTooltip
      {...(id ? { id } : {})}
      offset={offset}
      placement={placement}
      className={twMerge(
        className,
        'shadow-lg group z-header max-w-[20rem] rounded-xs bg-info-bg p-2 text-md leading-light text-info-fg outline-none tablet:max-w-[24rem]',
        'data-[entering]:animate-[fade-in_150ms_ease-out] data-[exiting]:animate-[fade-out_100ms_ease-in]',
      )}
      data-testid={dataTestId ?? 'tooltip'}
      aria-label={ariaLabel}
    >
      {showArrow && (
        <OverlayArrow>
          {({ placement: currentPlacement }) => (
            <TooltipArrow placement={currentPlacement} dataTestId={dataTestId} />
          )}
        </OverlayArrow>
      )}
      {children}
    </AriaTooltip>
  )
}
